import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory, withRouter } from 'react-router-dom';

import dayjs from 'utils/dayjs';
import DatePicker from 'components/commons/DatePicker';
import {
  DEFAULT_API_DATE_FORMAT,
  DEFAULT_SOURCE_CURRENCY,
  getCurrencyYearArchivePath,
  getDisplayValue,
  navigateCurrencyDatePicker,
  prefetchCurrencyYearArchive,
  shouldNavigateToCurrencyYearArchive,
} from 'utils';
import { getMbArchiveDayPath } from 'utils/mbArchive';

import { Table } from 'components/Table';
import { MbChart } from 'components/MbChart';
import { Update } from 'components/CurrencyNbuTable/styles';
import Icon from 'components/commons/Icon';
import { getMbComments, getSeoMeta } from 'selectors';
import { useSelectorProps } from 'hooks';
import { TableFooter } from 'components/Table/styles';
import {
  Wrapper,
  Info,
  HeaderMb,
  TitleWrapper,
  Title,
  WrapperDatePicker,
  SubTitle,
  ArchiveLink,
  Source,
} from './styles';
import { fetchBreadcrumbs, fetchSeoMeta } from '../../actions';

const titles = ['сurrency', 'purchase', 'sale'];

const terms = [
  { type: 'sell', value: 'interbank' },
  { type: 'buy', value: 'interbank' },
];

const getHref = currency => {
  const getCurrency = currency === 'usd' ? '' : `${currency}/`;
  return `/mb/${getCurrency}`;
};

const getArchivePath = currency => {
  const today = dayjs();

  if (shouldNavigateToCurrencyYearArchive(today)) {
    return getCurrencyYearArchivePath(currency, today.year());
  }

  return getMbArchiveDayPath(currency, today.format(DEFAULT_API_DATE_FORMAT));
};

const CurrencyMbTable = () => {
  const { formatMessage, locale } = useIntl();
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const selectorProps = useSelectorProps();
  const { currency = DEFAULT_SOURCE_CURRENCY } = params;
  const { pageTitle, pageDescription } = useSelector(state =>
    getSeoMeta(state, selectorProps)
  );
  const [date, setDate] = useState(params.date);
  const [isClosedMb, setIsClosedMb] = useState();

  useEffect(() => {
    dispatch(fetchSeoMeta(selectorProps));
    dispatch(fetchBreadcrumbs(selectorProps));
  }, [date, currency]);

  const currentCurrency = useSelector(state => {
    const list = (state.currency && state.currency.currencyList) || [];
    const found = list.find(
      el =>
        el &&
        el.name &&
        el.name.toLowerCase() === (currency || '').toLowerCase()
    );
    return found || null;
  });

  const onDateAccept = value => {
    navigateCurrencyDatePicker({
      history,
      selectedDate: value,
      currency,
      legacyPath: getMbArchiveDayPath(currency, value),
      locale,
    });
  };

  const onDatePickerOpen = () => {
    prefetchCurrencyYearArchive({
      selectedDate: date,
      currency,
      locale,
    });
  };

  const mbCommentsItem = useSelector(state => getMbComments(state));

  useEffect(() => {
    const isClose = mbCommentsItem?.USD?.[0]?.isClose;
    // Встановлюємо тільки якщо значення явно '0' або '1'
    if (isClose === '0' || isClose === '1') {
      setIsClosedMb(isClose);
    } else {
      setIsClosedMb(undefined);
    }
  }, [mbCommentsItem]);

  const currencySubTitle =
    (currentCurrency &&
      currentCurrency.text &&
      currentCurrency.text.split(' ')[0]) ||
    '';

  const archivePath = getArchivePath(currency);

  const sortedData =
    currency === 'eur'
      ? [mbCommentsItem?.EUR?.[0], mbCommentsItem?.USD?.[0]]
      : [mbCommentsItem?.USD?.[0], mbCommentsItem?.EUR?.[0]];

  const tableRows = sortedData.filter(Boolean);
  const hasTableData = tableRows.length > 0;
  const currencyLabel =
    (currentCurrency && currentCurrency.nameG) ||
    (currentCurrency && currentCurrency.name) ||
    (currency && currency.toUpperCase()) ||
    'USD';

  return (
    <Wrapper>
      <HeaderMb>
        <TitleWrapper>
          <Update>
            <Icon name="watch" style={{ margin: '0 5px 1px 0' }} />
            <FormattedMessage id="updated" />
            {` `}
            {(sortedData[0]?.timestamp || sortedData[0]?.datetime) &&
              dayjs(
                sortedData[0]?.timestamp || sortedData[0]?.datetime,
                'DD.MM.YYYY HH:mm:ss'
              ).format('DD.MM.YYYY, HH:mm')}
          </Update>

          <SubTitle>
            {pageDescription || (
              <>
                {formatMessage({
                  id: `mbPage.pageSubTitle`,
                })}
                {currencySubTitle ? ` - ` : ''}
                {currencySubTitle ? <span>{currencySubTitle}</span> : null}
              </>
            )}
          </SubTitle>

          <Title>
            {pageTitle ||
              formatMessage(
                {
                  id: `mbPage.pageTitle`,
                },
                { currency: currencyLabel }
              )}
          </Title>

          {(isClosedMb === '0' || isClosedMb === '1') && (
            <Info closed={isClosedMb}>
              {isClosedMb === '1'
                ? formatMessage({ id: 'table.mb.info.closed' })
                : formatMessage({ id: 'table.mb.info.open' })}
            </Info>
          )}
        </TitleWrapper>

        <WrapperDatePicker>
          <DatePicker
            type="outline"
            value={date}
            onChange={setDate}
            getDisplayValue={getDisplayValue}
            onOpen={onDatePickerOpen}
            onAccept={e => onDateAccept(e)}
          />
        </WrapperDatePicker>
      </HeaderMb>

      <Table
        titles={titles}
        rows={tableRows}
        type="mb"
        activeCurrency={currency}
        getHref={getHref}
        placeholder={!hasTableData}
      />

      <TableFooter>
        <Source>
          <FormattedMessage id="table.source.mb.desc" />
        </Source>

        <ArchiveLink to={archivePath}>
          <FormattedMessage id="mb.archive" />
        </ArchiveLink>
      </TableFooter>

      <MbChart
        type="interbank"
        terms={terms}
        chartTitle="dynamic.rates.chart.mb.title"
      />
    </Wrapper>
  );
};

export default withRouter(CurrencyMbTable);
